import { useEffect, useRef, useState } from "react";

/**
 * Renders its children only once they have been scrolled near.
 *
 * The trades list shows a chart on every card, and a few hundred closed trades
 * would otherwise mean a few hundred price requests and chart instances on
 * first paint. Mounting on approach keeps that to what is actually on screen,
 * and once mounted a card stays mounted so scrolling back is instant.
 */
export function LazyMount({
  children,
  minHeight = 280,
  placeholder = "Chart loads as you scroll…",
}: {
  children: React.ReactNode;
  minHeight?: number;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown || !ref.current) return;
    // No IntersectionObserver (old browser, jsdom) means show everything
    // rather than show nothing.
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.some((e) => e.isIntersecting) && setShown(true),
      // Start loading before it reaches the viewport, so scrolling at a normal
      // speed finds the chart already drawn.
      { rootMargin: "400px 0px" },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [shown]);

  return (
    <div ref={ref} style={shown ? undefined : { minHeight }}>
      {shown ? children : <div className="hint">{placeholder}</div>}
    </div>
  );
}
